// frontend/src/pages/builder/filters.js
// Phase 3 step 8: the builder's filters. Pure and unit-tested; no React.
//
// A filter in the builder is { id, field, op, value } with the value as the
// user typed it. toSpecFilters turns one into the spec's filters (the server
// validates every one again and binds every value as a parameter, see
// app/semantic/spec.py and compiler.py): numbers for numeric fields, ISO
// dates for date fields, lists for "any of". An incomplete or invalid filter
// gives [] and a `problem` from filterProblem, so nothing half-typed ever
// reaches the server. Operators follow the field's type, the way Power BI's
// filter cards do, and a date-and-time field is filtered by whole days.
import { chartableFields, isMeasureField, measureOnlyNotice } from './builderSpec';

// The server takes up to 20 filters (MAX_FILTERS in app/semantic/spec.py); a
// builder filter on a date-and-time field becomes two of them, so the builder
// stops at 10.
export const MAX_FILTERS = 10;
export const MAX_IN_VALUES = 200;       // MAX_IN_VALUES
export const MAX_VALUE_LENGTH = 500;    // MAX_STRING_VALUE

const NUMERIC = ['integer', 'number'];
const DATES = ['date', 'datetime'];

export const RELATIVE_PRESETS = [
  ['today', 'Today'], ['yesterday', 'Yesterday'],
  ['last_7_days', 'Last 7 days'], ['last_30_days', 'Last 30 days'], ['last_90_days', 'Last 90 days'],
  ['this_week', 'This week'], ['this_month', 'This month'], ['this_quarter', 'This quarter'], ['this_year', 'This year'],
  ['last_month', 'Last month'], ['last_quarter', 'Last quarter'], ['last_year', 'Last year'],
  ['last_12_months', 'Last 12 months'],
];
const RELATIVE_LABEL = Object.fromEntries(RELATIVE_PRESETS);

/** What kind of field, for the operator list and the value control. */
export function fieldKind(field) {
  if (!field) return 'text';
  if (NUMERIC.includes(field.data_type)) return 'number';
  if (DATES.includes(field.data_type)) return 'date';
  if (field.data_type === 'boolean') return 'boolean';
  return 'text';
}

const TEXT_OPS = [
  ['eq', 'is'], ['neq', 'is not'], ['in', 'is any of'], ['not_in', 'is none of'],
  ['contains', 'contains'], ['starts_with', 'starts with'], ['is_null', 'is blank'], ['not_null', 'is not blank'],
];
const NUMBER_OPS = [
  ['eq', 'is'], ['neq', 'is not'], ['gt', 'is more than'], ['gte', 'is at least'], ['lt', 'is less than'], ['lte', 'is at most'],
  ['between', 'is between'], ['in', 'is any of'], ['is_null', 'is blank'], ['not_null', 'is not blank'],
];
const DATE_OPS = [
  ['relative', 'is in the'], ['eq', 'is on'], ['gte', 'is on or after'], ['lte', 'is on or before'],
  ['gt', 'is after'], ['lt', 'is before'], ['between', 'is between'], ['is_null', 'is blank'], ['not_null', 'is not blank'],
];
const BOOLEAN_OPS = [['eq', 'is'], ['is_null', 'is blank'], ['not_null', 'is not blank']];

/** [[op, label], ...] for a field. */
export function operatorsFor(field) {
  switch (fieldKind(field)) {
    case 'number': return NUMBER_OPS;
    case 'date': return DATE_OPS;
    case 'boolean': return BOOLEAN_OPS;
    default: return TEXT_OPS;
  }
}

export const operatorLabel = (field, op) => (operatorsFor(field).find(([o]) => o === op) || [op, op])[1];

/** How many values the operator takes: 0, 1, 2 (between) or 'list'. */
export function valueShape(op) {
  if (op === 'is_null' || op === 'not_null') return 0;
  if (op === 'between') return 2;
  if (op === 'in' || op === 'not_in') return 'list';
  return 1;
}

let nextId = 1;
/** A new filter on a field with the type's usual operator and an empty value. */
export function newFilter(field) {
  const kind = fieldKind(field);
  const op = kind === 'date' ? 'relative' : kind === 'number' ? 'gte' : 'eq';
  const value = kind === 'date' ? 'last_30_days' : kind === 'boolean' ? 'true' : '';
  nextId += 1;
  return { id: `f${nextId}`, field: field.key, op, value };
}

/** Change the operator, keeping the value when the new operator takes the same shape. */
export function setOperator(filter, field, op) {
  if (!operatorsFor(field).some(([o]) => o === op)) return filter;
  const same = valueShape(filter.op) === valueShape(op) && filter.op !== 'relative' && op !== 'relative';
  let value = same ? filter.value : '';
  if (op === 'relative') value = 'last_30_days';
  if (op === 'between' && !Array.isArray(value)) value = ['', ''];
  if (fieldKind(field) === 'boolean' && op === 'eq' && value === '') value = 'true';
  return { ...filter, op, value };
}

/** The list a user typed for "any of": one value per line or comma, trimmed, no blanks or repeats. */
export function parseList(text) {
  const out = [];
  for (const raw of String(text || '').split(/[\n,]/)) {
    const v = raw.trim();
    if (v && !out.includes(v)) out.push(v);
  }
  return out;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const nextDay = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

/** One typed value -> the value sent, or undefined when it is not usable for this kind. */
function coerce(kind, raw) {
  const text = String(raw == null ? '' : raw).trim();
  if (!text) return undefined;
  if (text.length > MAX_VALUE_LENGTH) return undefined;
  if (kind === 'number') { const n = Number(text); return Number.isFinite(n) ? n : undefined; }
  if (kind === 'date') return ISO_DAY.test(text) ? text : undefined;
  if (kind === 'boolean') return text === 'true' ? true : text === 'false' ? false : undefined;
  return text;
}

/** Why a filter can't be sent yet, or null. */
export function filterProblem(filter, field) {
  if (!field) return 'This field is no longer in the dataset.';
  if (!operatorsFor(field).some(([o]) => o === filter.op)) return 'Choose how to filter.';
  const kind = fieldKind(field);
  const shape = valueShape(filter.op);
  if (shape === 0) return null;
  if (filter.op === 'relative') return RELATIVE_LABEL[filter.value] ? null : 'Choose a period.';
  const need = kind === 'number' ? 'Enter a number.' : kind === 'date' ? 'Pick a date.' : 'Enter a value.';
  if (shape === 'list') {
    const items = parseList(filter.value);
    if (!items.length) return 'Enter one or more values.';
    if (items.length > MAX_IN_VALUES) return `Up to ${MAX_IN_VALUES} values.`;
    return items.every((v) => coerce(kind, v) !== undefined) ? null : need;
  }
  if (shape === 2) {
    const [a, b] = Array.isArray(filter.value) ? filter.value : ['', ''];
    const ca = coerce(kind, a);
    const cb = coerce(kind, b);
    if (ca === undefined || cb === undefined) return kind === 'number' ? 'Enter two numbers.' : 'Pick two dates.';
    if (ca > cb) return 'The first value must not be larger than the second.';
    return null;
  }
  return coerce(kind, filter.value) === undefined ? need : null;
}

/**
 * The spec filters for one builder filter: [] when it has a problem. A
 * date-and-time field is filtered by whole days ("on the 5th" is the 5th
 * from midnight to midnight), so an "is on" or "is between" becomes a range
 * and "after the 5th" starts on the 6th.
 */
export function toSpecFilters(filter, field) {
  if (filterProblem(filter, field)) return [];
  const kind = fieldKind(field);
  const { op } = filter;
  const key = field.key;
  if (op === 'is_null' || op === 'not_null') return [{ field: key, op }];
  if (op === 'relative') return [{ field: key, op, value: filter.value }];
  if (op === 'in' || op === 'not_in') return [{ field: key, op, value: parseList(filter.value).map((v) => coerce(kind, v)) }];
  const timestamps = field.data_type === 'datetime';
  if (op === 'between') {
    const [a, b] = filter.value.map((v) => coerce(kind, v));
    if (timestamps) return [{ field: key, op: 'gte', value: a }, { field: key, op: 'lt', value: nextDay(b) }];
    return [{ field: key, op, value: [a, b] }];
  }
  const v = coerce(kind, filter.value);
  if (timestamps) {
    if (op === 'eq') return [{ field: key, op: 'gte', value: v }, { field: key, op: 'lt', value: nextDay(v) }];
    if (op === 'lte') return [{ field: key, op: 'lt', value: nextDay(v) }];
    if (op === 'gt') return [{ field: key, op: 'gte', value: nextDay(v) }];
  }
  return [{ field: key, op, value: v }];
}

/** The spec's `filters` for every complete builder filter, in order. */
export function specFilters(filters, fieldsByKey) {
  const out = [];
  for (const f of filters || []) out.push(...toSpecFilters(f, fieldsByKey[f.field]));
  return out;
}

/** Add `filters` to a spec (dropping the key when there are none). */
export function withFilters(spec, filters) {
  if (!spec) return spec;
  if (!filters || !filters.length) { const { filters: gone, ...rest } = spec; return rest; }   // eslint-disable-line no-unused-vars
  return { ...spec, filters };
}

/** "Department is Engineering": the filter in words, for the panel and the chart's subtitle. */
export function filterSummary(filter, field) {
  const name = (field && field.display_name) || filter.field;
  const op = operatorLabel(field, filter.op);
  const shape = valueShape(filter.op);
  if (shape === 0) return `${name} ${op}`;
  if (filter.op === 'relative') return `${name} ${op} ${(RELATIVE_LABEL[filter.value] || '…').toLowerCase()}`;
  if (shape === 'list') {
    const items = parseList(filter.value);
    const shown = items.slice(0, 3).join(', ');
    return `${name} ${op} ${items.length > 3 ? `${shown} +${items.length - 3}` : shown || '…'}`;
  }
  if (shape === 2) {
    const [a, b] = Array.isArray(filter.value) ? filter.value : ['', ''];
    return `${name} ${op} ${a || '…'} and ${b || '…'}`;
  }
  const v = String(filter.value == null ? '' : filter.value).trim();
  return `${name} ${op} ${v || '…'}`;
}

const prevDay = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};
const dayText = (v) => (typeof v === 'string' && ISO_DAY.test(v) ? v : null);

/**
 * A saved spec's filters -> builder filters (step 9). The two-sided day
 * ranges toSpecFilters makes for a date-and-time field come back as the one
 * card they came from ("is on" / "is between"); everything else maps one to
 * one, with lists joined for the text box. A filter on a field the dataset
 * no longer has keeps its field key, so the card can say so.
 */
export function fromSpecFilters(list, fieldsByKey = {}) {
  const out = [];
  const items = Array.isArray(list) ? list : [];
  for (let i = 0; i < items.length; i += 1) {
    const f = items[i];
    if (!f || typeof f.field !== 'string') continue;
    const field = fieldsByKey[f.field];
    const next = items[i + 1];
    if (field && field.data_type === 'datetime' && f.op === 'gte' && next && next.field === f.field && next.op === 'lt'
      && dayText(f.value) && dayText(next.value)) {
      const to = prevDay(next.value);
      out.push(to === f.value
        ? { ...newFilter(field), op: 'eq', value: f.value }
        : { ...newFilter(field), op: 'between', value: [f.value, to] });
      i += 1;
      continue;
    }
    const base = field ? newFilter(field) : { id: `f${(nextId += 1)}`, field: f.field, op: f.op, value: '' };
    let { op } = f;
    let value = f.value;
    if (field && field.data_type === 'datetime' && dayText(value)) {
      if (op === 'lt') { op = 'lte'; value = prevDay(value); }            // "is before the 6th" was "on or before the 5th"
    }
    const shape = valueShape(op);
    if (shape === 0) value = '';
    else if (shape === 'list') value = (Array.isArray(value) ? value : [value]).map((v) => String(v)).join('\n');
    else if (shape === 2) value = Array.isArray(value) ? value.map((v) => String(v)) : ['', ''];
    else value = value === null || value === undefined ? '' : String(value);
    out.push({ ...base, field: f.field, op, value });
  }
  return out;
}

/** Fields a filter can be added on: the chartable ones, in list order. */
export const filterableFields = (fields) => chartableFields(fields).filter((f) => !isMeasureField(f));   // a saved measure is a total, not a row value (13c-3)

/** Add a filter on a field; null and a notice past the limit, or for a saved measure. */
export function addFilter(filters, field) {
  if (isMeasureField(field)) return { filters, notice: measureOnlyNotice(field) };
  if (filters.length >= MAX_FILTERS) return { filters, notice: `A chart can have up to ${MAX_FILTERS} filters.` };
  return { filters: [...filters, newFilter(field)], notice: null };
}
